export type StepStatus = "wait" | "process" | "finish";

export type ProgressStep = {
  title: string;
  status: StepStatus;
};

export type ProgressBarTheme = "blue" | "orange";

interface ProgressBarProps {
  steps: ProgressStep[];
  theme?: ProgressBarTheme;
  size?: "small" | "normal";
}

const THEME_COLORS = {
  blue: {
    finish: "#1E3A8A",
    process: "#2563EB",
    line: "rgba(255,255,255,0.6)",
    lineInactive: "rgba(255,255,255,0.1)",
    textActive: "rgba(255,255,255,0.9)",
    textInactive: "rgba(255,255,255,0.3)",
    bgInactive: "rgba(255,255,255,0.12)",
    colorInactive: "rgba(255,255,255,0.3)",
    borderInactive: "1.5px solid rgba(255,255,255,0.15)",
  },
  orange: {
    finish: "#FF6B35",
    process: "#FF6B35",
    line: "#FF6B35",
    lineInactive: "#F0F0F0",
    textActive: "#FF6B35",
    textInactive: "#CACACA",
    bgInactive: "#F0F0F0",
    colorInactive: "#C4C8D0",
    borderInactive: "none",
  },
};

function getStepStyle(
  status: StepStatus,
  theme: ProgressBarTheme
): { bg: string; color: string; border: string } {
  const colors = THEME_COLORS[theme];
  switch (status) {
    case "finish":
      return { bg: colors.finish, color: "#fff", border: colors.borderInactive };
    case "process":
      return { bg: colors.process, color: "#fff", border: colors.borderInactive };
    case "wait":
    default:
      return { bg: colors.bgInactive, color: colors.colorInactive, border: colors.borderInactive };
  }
}

export function ProgressBar({ steps, theme = "blue", size = "normal" }: ProgressBarProps) {
  const colors = THEME_COLORS[theme];
  const dotSize = size === "small" ? 18 : 22;
  const fontSize = size === "small" ? 8 : 10;
  const lineMargin = size === "small" ? "mb-[14px]" : "mb-[18px]";

  const getLineStyle = (
    currentStatus: StepStatus,
    nextStatus: StepStatus
  ) => {
    if (theme === "blue") {
      if (currentStatus === "finish" && nextStatus === "finish") return colors.line;
      if (currentStatus === "finish" || nextStatus === "finish") return "rgba(255,255,255,0.4)";
      return colors.lineInactive;
    }
    if (currentStatus === "finish") return colors.line;
    return colors.lineInactive;
  };

  return (
    <div className="flex items-center">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const stepStyle = getStepStyle(step.status, theme);

        return (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: dotSize,
                  height: dotSize,
                  background: stepStyle.bg,
                  color: stepStyle.color,
                  fontSize,
                  fontWeight: 700,
                  border: stepStyle.border,
                }}
              >
                {step.status === "finish" ? (
                  <i className="ri-check-line" style={{ fontSize: fontSize + 1 }} />
                ) : step.status === "process" ? (
                  <i className="ri-loader-4-line animate-spin" style={{ fontSize }} />
                ) : (
                  <span>{i + 1}</span>
                )}
              </div>
              <span
                className="text-center mt-1 leading-none"
                style={{
                  fontSize: size === "small" ? 8 : 9,
                  color:
                    step.status !== "wait"
                      ? colors.textActive
                      : colors.textInactive,
                  whiteSpace: "nowrap",
                  fontWeight: step.status === "process" ? 600 : 400,
                }}
              >
                {step.title}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 ${lineMargin} mx-0.5`}
                style={{
                  height: size === "small" ? 1.5 : 1.5,
                  background: getLineStyle(step.status, steps[i + 1].status),
                  borderRadius: 2,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** 根据 step 数字生成步骤配置（兼容旧用法） */
export function ProgressBarByStep({
  step,
  titles,
  theme = "blue",
  size,
}: {
  step: number;
  titles: string[];
  theme?: ProgressBarTheme;
  size?: "small" | "normal";
}) {
  const steps: ProgressStep[] = titles.map((title, i) => ({
    title,
    status: i < step ? "finish" : i === step ? "process" : "wait",
  }));
  return <ProgressBar steps={steps} theme={theme} size={size} />;
}
